/**
 * <complaint-form>: the form on /pages/reklamation (sections/complaint-form.liquid).
 *
 * Talks to the Gadget app schindele-retoure through the Shopify app proxy
 * (data-proxy, "/apps/reklamation" or "/en/apps/reklamation"):
 *   POST {proxy}/order   { orderName, email }                         -> items to pick, contact to prefill
 *   POST {proxy}/upload  { uploadToken, kind, fileName, mimeType, base64 } -> { id }
 *   POST {proxy}/submit  the whole form as JSON with the ids of the uploaded files
 *
 * Photos are downsized in the browser (longest edge 1600 px, JPEG) before the
 * upload, so a phone photo of 4 MB becomes 200 to 600 KB and the upload works
 * on a slow mobile connection. Each file goes up on its own as soon as it is
 * chosen; the submit only sends the ids. One upload token per form session
 * binds the files to this submit (the app refuses files of another token).
 *
 * Two modes (data-mode): "customer" on /pages/reklamation, "staff" on
 * /pages/reklamation-annahme where an employee types for the customer at
 * the counter (Bearbeiter, "Wo ist der Artikel jetzt?": in the store with the store name, or still with the customer; the channel
 * "internal" for defective goods without a customer, a printable slip).
 *
 * Since 06.10.2026 (Joscha, call 06.10.) also purchases the app cannot look
 * up: the previous webshop (orders up to 08.09.2026, "oldshop") and eBay
 * ("ebay") with the invoice or eBay order number, the date and the receipt;
 * and an online order the lookup did not find ("Trotzdem melden", kind
 * "unverified"). The office checks those in Sage or the Shopify admin. Which
 * steps and fields a purchase needs is one table (KIND_STEPS, RECEIPT_KINDS);
 * elements inside a step that only fit some kinds carry data-only="kind ...".
 * ?kauf=online|altshop|ebay|laden preselects the card (returns page, KB).
 *
 * Since 19.10.2026 (Joscha: no submit button for "Im Geschäft", "Im früheren
 * Onlineshop" and "Bei eBay") the rest of the form opens in one go as soon as
 * the purchase is clear: with the card for every purchase without a Shopify
 * order, with the picked item online. Before, serial, defect and contact
 * (and with them the button) waited for date, store or number, manufacturer
 * and a model of two characters; one gap left the form ending without a hint.
 * Whatever is missing now shows on submit, next to its field.
 *
 * Pure helpers are exported for tests/complaint-form.test.mjs; the element
 * takes an `imageProcessor` so the tests can bypass the canvas.
 */

export const MAX_EDGE = 1600;
export const JPEG_QUALITY = 0.85;
export const MAX_FILE_BYTES = 8 * 1024 * 1024;
/** The shortest defect description the app accepts (DESCRIPTION_MIN in the app's complaintRules). */
export const DESCRIPTION_MIN = 20;

export function randomToken(bytes = crypto.getRandomValues(new Uint8Array(32))) {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function normalizeOrderName(value) {
  const s = String(value ?? '').trim().replace(/^#/, '');
  return /^\d{1,8}$/.test(s) ? `#${s}` : null;
}

export function fill(template, values) {
  return String(template ?? '').replace(/\{(\w+)\}/g, (_, key) => (values[key] == null ? '' : String(values[key])));
}

/** The old webshop's last order day; Shopify orders start the day after (mirrors api/lib/complaintRules.ts). */
export const OLDSHOP_LAST_DAY = '2026-09-08';
export const NEWSHOP_FIRST_DAY = '2026-09-09';
/** The app refuses purchase days before this one and days in the future (purchaseDayOf in complaintRules). */
export const FIRST_PURCHASE_DAY = '2000-01-01';

/** A date as the date input writes it (YYYY-MM-DD), in the browser's time zone. */
export function localDay(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
/**
 * The number of an old-shop or eBay purchase in its printed form, or null;
 * mirrors normalizePurchaseRef in the app (api/lib/complaintRules.ts). From
 * two sample invoices (06.10.2026), both from Sage: the Belegnummer top right
 * (2026-3101234), and under "Ihr Beleg" the old shop's order number
 * (S01-260228-123456) or the eBay order number (12-34567-89012). Spaces, dots
 * and slashes count as a dash, and the Belegnummer and S01 number may come
 * without dashes; the eBay order number keeps them, because twelve digits in
 * one piece are the item number (isEbayItemNumber). A digit too few is a typo.
 */
export function normalizePurchaseRef(channel, value) {
  const s = String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/^#/, '')
    .replace(/[\s./\u2010-\u2015\u2212-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  let m = /^(20\d{2})-?(\d{7})$/.exec(s);
  if (m && (channel === 'oldshop' || channel === 'ebay')) return `${m[1]}-${m[2]}`;
  m = /^(S\d{2})-?(\d{6})-?(\d{4,8})$/.exec(s);
  if (m && channel === 'oldshop') return `${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{2})-(\d{5})-(\d{5})$/.exec(s);
  if (m && channel === 'ebay') return `${m[1]}-${m[2]}-${m[3]}`;
  return null;
}

/** Twelve digits in one piece: the eBay item number ("ebay Nummer" on our invoice), easily taken for the order number. */
export function isEbayItemNumber(value) {
  return /^#?\d{12}$/.test(String(value ?? '').trim());
}

/** How the item was bought: the channel, and an online order the lookup could not find as its own kind. */
export function purchaseKind(state) {
  if (state.channel === 'online') return state.orderUnverified ? 'unverified' : 'online';
  return state.channel || null;
}

/** The purchase-dependent steps per kind; intake, channel, serial, defect and contact are shared. */
export const KIND_STEPS = {
  online: ['online'],
  unverified: ['online', 'purchase', 'item'],
  store: ['purchase', 'item'],
  oldshop: ['purchase', 'item'],
  ebay: ['purchase', 'item'],
  internal: ['item'],
};
/** Kinds proved by a receipt: a customer must upload one, staff may record that it is missing. */
export const RECEIPT_KINDS = ['store', 'oldshop', 'ebay', 'unverified'];
const PURCHASE_STEPS = ['online', 'purchase', 'item'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** ?kauf= on the page URL: links from the returns page and the KB open the right card. */
export const DEEP_LINK_CHANNELS = { online: 'online', altshop: 'oldshop', ebay: 'ebay', laden: 'store', geschaeft: 'store' };

/** What the browser checks before it asks the app, with the same codes the app answers. */
export function validateLocal(state, limits, mode = 'customer', today = localDay()) {
  const errors = {};
  const staff = mode === 'staff';
  const kind = purchaseKind(state);
  const internal = staff && kind === 'internal';
  if (staff) {
    if (String(state.intakeBy ?? '').trim().length < 2) errors.intakeBy = 'required';
    // Asked every time (no default): "beim Kunden" is what lets the office send a label later.
    if (!internal && state.itemWhere !== 'house' && state.itemWhere !== 'customer') errors.itemWhere = 'where';
    if ((state.itemWhere === 'house' || internal) && !state.itemLocation) errors.itemLocation = 'required';
  }
  if (!kind || !(kind in KIND_STEPS) || (kind === 'internal' && !staff)) errors.channel = 'channel';
  if (kind === 'online' || kind === 'unverified') {
    if (!normalizeOrderName(state.orderName)) errors.orderName = 'invalid';
    if (!EMAIL_PATTERN.test(String(state.orderEmail ?? '').trim())) errors.orderEmail = 'invalid';
    if (kind === 'online' && !state.itemKey) errors.itemKey = 'item';
  }
  if (kind === 'oldshop' || kind === 'ebay') {
    // A wrong format gets the example of its own kind next to the field, the eBay item number its own hint
    // (the app answers "invalid" or "ebay_item").
    const raw = String(state.purchaseRef ?? '').trim();
    if (!normalizePurchaseRef(kind, raw)) {
      errors.purchaseRef = !raw ? 'required' : kind === 'ebay' && isEbayItemNumber(raw) ? 'ebay_item' : `ref_${kind}`;
    }
  }
  if (RECEIPT_KINDS.includes(kind)) {
    const day = String(state.purchaseDate ?? '');
    if (!DAY_PATTERN.test(day)) errors.purchaseDate = kind === 'store' ? 'invalid' : 'required';
    // "1.10.26" typed into the date field becomes the year 0026; a future day is a slip as well.
    else if (day < FIRST_PURCHASE_DAY || day > today) errors.purchaseDate = 'invalid';
    else if (kind === 'oldshop' && day > OLDSHOP_LAST_DAY) errors.purchaseDate = 'after_oldshop';
    else if (kind === 'unverified' && day < NEWSHOP_FIRST_DAY) errors.purchaseDate = 'before_newshop';
    if (kind === 'store' && !state.storeLocation) errors.storeLocation = 'required';
    if ((state.receiptIds ?? []).length === 0 && !(staff && state.receiptMissing)) errors.receiptIds = 'required';
  }
  if (kind && kind !== 'online') {
    if (String(state.manufacturer ?? '').trim().length < 2) errors.manufacturer = 'required';
    if (String(state.itemTitle ?? '').trim().length < 2) errors.itemTitle = 'required';
  }
  if (!state.serialMissing && !String(state.serialNumber ?? '').trim()) errors.serialNumber = 'required';
  const description = String(state.defectDescription ?? '').trim();
  if (description.length < DESCRIPTION_MIN) errors.defectDescription = description ? 'too_short' : 'required';
  if ((state.photoIds ?? []).length < limits.minPhotos) errors.photoIds = 'too_few';
  if ((state.photoIds ?? []).length > limits.maxPhotos) errors.photoIds = 'too_many';
  // Goods from our own stock: no customer, so no contact fields and no privacy box (Joscha, 24.10.2026).
  if (internal) return errors;
  if (!/\s/.test(String(state.customerName ?? '').trim())) errors.customerName = 'required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(state.email ?? '').trim())) errors.email = 'invalid';
  if (String(state.phone ?? '').replace(/\D/g, '').length < 6) errors.phone = 'invalid';
  const address1 = String(state.address1 ?? '').trim();
  if (address1.length < 4) errors.address1 = 'required';
  else if (!/\d/.test(address1)) errors.address1 = 'no_house_number';
  if (!String(state.zip ?? '').trim()) errors.zip = 'invalid';
  if (String(state.city ?? '').trim().length < 2) errors.city = 'required';
  if (!state.countryCode) errors.countryCode = 'invalid';
  if (!state.privacyAccepted) errors.privacyAccepted = 'privacy';
  return errors;
}

/** The JSON body of /submit. */
export function buildPayload(state, uploadToken, locale, mode = 'customer') {
  const kind = purchaseKind(state);
  const internal = kind === 'internal';
  const staff = mode === 'staff';
  const withReceipt = RECEIPT_KINDS.includes(kind);
  const orderName = normalizeOrderName(state.orderName) ?? String(state.orderName ?? '');
  const orderEmail = String(state.orderEmail ?? '').trim();
  return {
    uploadToken,
    locale,
    channel: state.channel,
    intake: staff
      ? { mode: 'staff', by: String(state.intakeBy ?? '').trim(), code: String(state.intakeCode ?? '').trim(), itemInHouse: state.itemWhere === 'house' || internal, itemLocation: state.itemWhere === 'house' || internal ? state.itemLocation ?? null : null }
      : null,
    order: kind === 'online' ? { orderName, email: orderEmail, itemKey: state.itemKey ?? null } : kind === 'unverified' ? { orderName, email: orderEmail, unverified: true } : null,
    store: kind === 'store' ? { purchaseDate: state.purchaseDate ?? '', location: state.storeLocation ?? '' } : null,
    purchase:
      kind === 'oldshop' || kind === 'ebay'
        ? { purchaseDate: state.purchaseDate ?? '', reference: String(state.purchaseRef ?? '').trim(), customerReference: String(state.purchaseCustomerRef ?? '').trim() }
        : kind === 'unverified'
          ? { purchaseDate: state.purchaseDate ?? '' }
          : null,
    receiptMissing: staff && withReceipt ? Boolean(state.receiptMissing) : false,
    article: kind === 'online' ? null : { manufacturer: String(state.manufacturer ?? '').trim(), title: String(state.itemTitle ?? '').trim(), variantTitle: String(state.variantTitle ?? '').trim(), sku: String(state.sku ?? '').trim() },
    serialNumber: String(state.serialNumber ?? '').trim(),
    serialMissing: Boolean(state.serialMissing),
    defect: { description: String(state.defectDescription ?? '').trim(), occurredAt: String(state.occurredAt ?? '').trim() },
    // Nothing typed into the contact fields of another card travels with a case from stock.
    contact: internal
      ? null
      : {
          name: String(state.customerName ?? '').trim(),
          email: String(state.email ?? '').trim(),
          phone: String(state.phone ?? '').trim(),
          address1: String(state.address1 ?? '').trim(),
          address2: String(state.address2 ?? '').trim(),
          zip: String(state.zip ?? '').trim(),
          city: String(state.city ?? '').trim(),
          countryCode: String(state.countryCode ?? 'DE'),
        },
    photoIds: [...(state.photoIds ?? [])],
    receiptIds: withReceipt ? [...(state.receiptIds ?? [])] : [],
    privacyAccepted: internal ? false : Boolean(state.privacyAccepted),
    website: String(state.website ?? ''),
  };
}

/** The field order on the page, for the error summary. */
export const FIELD_ORDER = ['intakeBy', 'intakeCode', 'itemWhere', 'itemLocation', 'channel', 'orderName', 'orderEmail', 'itemKey', 'purchaseRef', 'purchaseCustomerRef', 'purchaseDate', 'storeLocation', 'receiptIds', 'manufacturer', 'itemTitle', 'serialNumber', 'defectDescription', 'photoIds', 'customerName', 'email', 'phone', 'address1', 'zip', 'city', 'countryCode', 'privacyAccepted'];

/** Where the staff tablet remembers the Bearbeiter, the code and the store between cases. */
export const STAFF_STORAGE_KEYS = { intakeBy: 'complaint.intakeBy', intakeCode: 'complaint.intakeCode', itemLocation: 'complaint.itemLocation' };

function readStorage(key) {
  try {
    return window.localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, String(value ?? ''));
  } catch {
    /* private mode or blocked storage: the staff member types it again */
  }
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

/**
 * Downsizes an image to MAX_EDGE and re-encodes it as JPEG. Returns
 * { base64, mimeType, fileName, previewUrl }. Throws 'unreadable' when the
 * browser cannot decode the file (HEIC outside Safari, a corrupt file).
 */
export async function processImage(file) {
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('unreadable');
  }
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();
  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  return { base64: dataUrl.split(',')[1], mimeType: 'image/jpeg', fileName: String(file.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg', previewUrl: dataUrl };
}

/** A PDF receipt goes up as it is. */
export async function processDocument(file) {
  if (file.size > MAX_FILE_BYTES) throw new Error('too_large');
  const dataUrl = await readFileAsDataUrl(file);
  return { base64: dataUrl.split(',')[1], mimeType: file.type || 'application/pdf', fileName: file.name || 'Kaufbeleg.pdf', previewUrl: null };
}

export class ComplaintForm extends HTMLElement {
  constructor() {
    super();
    this.state = { channel: null, photoIds: [], receiptIds: [], countryCode: 'DE', itemWhere: null, orderUnverified: false };
    this.files = { photo: [], receipt: [] };
    this.uploads = new Set();
    this.uploadToken = randomToken();
    this.imageProcessor = processImage;
    this.documentProcessor = processDocument;
    this.fetchImpl = (...args) => fetch(...args);
  }

  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.proxy = String(this.dataset.proxy || '/apps/reklamation').replace(/\/+$/, '');
    this.mode = this.dataset.mode === 'staff' ? 'staff' : 'customer';
    this.locale = (this.dataset.locale || 'de').toLowerCase().startsWith('en') ? 'en' : 'de';
    this.limits = { minPhotos: Number(this.dataset.minPhotos || 3), maxPhotos: Number(this.dataset.maxPhotos || 6), maxReceipts: Number(this.dataset.maxReceipts || 2) };
    try {
      this.strings = JSON.parse(this.querySelector('[data-complaint-strings]')?.textContent || '{}');
    } catch {
      this.strings = {};
    }
    this.form = this.querySelector('[data-complaint-form]');
    if (!this.form) return;
    this.form.addEventListener('input', (event) => this.onInput(event));
    this.form.addEventListener('change', (event) => this.onInput(event));
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submit();
    });
    this.querySelector('[data-lookup]')?.addEventListener('click', () => void this.lookup());
    this.querySelector('[data-unverified-toggle]')?.addEventListener('click', () => this.setUnverified(true));
    for (const input of this.querySelectorAll('[data-file-input]')) {
      input.addEventListener('change', () => {
        void this.addFiles(input.dataset.fileInput, Array.from(input.files || []));
        input.value = '';
      });
    }
    if (this.mode === 'staff') this.prefillStaff();
    // The picker offers only days the app accepts.
    const purchaseDate = this.querySelector('[data-field="purchaseDate"]');
    if (purchaseDate) {
      purchaseDate.min = FIRST_PURCHASE_DAY;
      purchaseDate.max = localDay();
    }
    this.querySelector('[data-slip-print]')?.addEventListener('click', () => window.print());
    this.querySelector('[data-next-case]')?.addEventListener('click', () => window.location.reload());
    this.querySelector('[data-item-list]')?.addEventListener('change', (event) => {
      const target = event.target;
      if (target?.name === 'itemKey') {
        this.state.itemKey = target.value;
        this.clearError('itemKey');
        this.revealFrom('serial');
      }
    });
    this.renderPhotoCount();
    this.preselect();
  }

  /** ?kauf=altshop and friends: the card is chosen, the customer starts in the right step. */
  preselect() {
    let wanted = '';
    try {
      wanted = new URLSearchParams(window.location.search).get('kauf') ?? '';
    } catch {
      wanted = '';
    }
    const channel = DEEP_LINK_CHANNELS[wanted.trim().toLowerCase()];
    const radio = channel ? this.querySelector(`[data-field="channel"][value="${channel}"]`) : null;
    if (!radio) return;
    radio.checked = true;
    this.state.channel = channel;
    this.setChannel(channel);
  }

  /** The tablet keeps Bearbeiter, code and store from the last case. */
  prefillStaff() {
    for (const [field, key] of Object.entries(STAFF_STORAGE_KEYS)) {
      const input = this.querySelector(`[data-field="${field}"]`);
      const value = readStorage(key);
      if (input && value) {
        input.value = value;
        this.state[field] = value;
      }
    }
    this.toggleItemLocation();
  }

  toggleItemLocation() {
    const internal = this.state.channel === 'internal';
    const wrap = this.querySelector('[data-item-location]');
    if (wrap) wrap.hidden = !(this.state.itemWhere === 'house' || internal);
    // Internal goods are always in the house: the question does not apply.
    const where = this.querySelector('[data-item-where]');
    if (where) where.hidden = internal;
    const note = this.querySelector('[data-item-customer-note]');
    if (note) note.hidden = internal || this.state.itemWhere !== 'customer';
  }

  // --- state -------------------------------------------------------------

  onInput(event) {
    const target = event.target;
    const field = target?.dataset?.field;
    if (!field) return;
    if (target.type === 'checkbox') this.state[field] = target.checked;
    else if (target.type === 'radio') {
      if (target.checked) this.state[field] = target.value;
    } else this.state[field] = target.value;
    this.clearError(field);
    if (field in STAFF_STORAGE_KEYS) writeStorage(STAFF_STORAGE_KEYS[field], this.state[field]);
    if (field === 'itemWhere') this.toggleItemLocation();
    if (field === 'channel') this.setChannel(target.value);
    if (field === 'serialMissing') {
      const serial = this.querySelector('[data-field="serialNumber"]');
      if (serial) {
        serial.disabled = target.checked;
        if (target.checked) {
          serial.value = '';
          this.state.serialNumber = '';
        }
      }
    }
    // Joscha, 15.10.2026: a description under 20 characters left no button and no hint. The counter says what is
    // missing; submitting a too short text marks the field (validateLocal).
    if (field === 'defectDescription') this.renderDescriptionHint();
  }

  setChannel(channel) {
    this.state.channel = channel;
    // A new card is a new purchase: the old card's errors would point the wrong way (the date field is shared, so a
    // store date stayed flagged "choose the previous webshop" after the switch).
    this.clearAllErrors();
    // "Trotzdem melden" belongs to the online search it followed; a new card starts clean.
    this.state.orderUnverified = false;
    this.applyKind();
    if (channel === 'online') {
      this.state.itemKey = null;
      if (this.lookedUp) this.revealFrom('serial');
    }
  }

  /** Shows the steps and the kind-specific labels, fields and texts of the current purchase kind; folds the later steps. */
  applyKind() {
    const kind = purchaseKind(this.state);
    const steps = KIND_STEPS[kind] ?? [];
    for (const key of PURCHASE_STEPS) this.showStep(key, steps.includes(key));
    for (const key of ['serial', 'defect', 'contact']) this.showStep(key, false);
    for (const el of this.querySelectorAll('[data-only]')) el.hidden = !String(el.dataset.only).split(/\s+/).includes(kind);
    const internal = kind === 'internal';
    // Goods from our own stock have no customer: the last step is only the submit button.
    for (const el of this.querySelectorAll('[data-contact-title], [data-contact-fields], [data-privacy], [data-submit-note]')) el.hidden = internal;
    for (const el of this.querySelectorAll('[data-contact-title-internal], [data-contact-none]')) el.hidden = !internal;
    this.toggleItemLocation();
    this.renderUnverified();
    // Without a Shopify order there is nothing to look up first: the whole form is open with the card.
    if (kind && kind !== 'online') this.revealFrom('serial');
  }

  /** Online order not found: the customer files anyway with date, receipt and article; the office checks by hand. */
  setUnverified(on) {
    this.state.orderUnverified = Boolean(on) && this.state.channel === 'online';
    if (this.state.orderUnverified) {
      this.renderItems(null);
      this.clearError('orderName');
    }
    this.applyKind();
    if (this.state.orderUnverified) this.querySelector('[data-step="purchase"]')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }

  /** The offer after a lookup that found nothing, and the note once the customer took it. */
  renderUnverified() {
    const offer = this.querySelector('[data-unverified]');
    if (offer) offer.hidden = !(this.lookupMissed && this.state.channel === 'online' && !this.state.orderUnverified);
    const active = this.querySelector('[data-unverified-active]');
    if (active) active.hidden = !this.state.orderUnverified;
  }

  showStep(key, visible) {
    const step = this.querySelector(`[data-step="${key}"]`);
    if (step) step.hidden = !visible;
  }

  /** Reveals a step and every step after it, down to the submit button. */
  revealFrom(key) {
    const order = ['serial', 'defect', 'contact'];
    const index = order.indexOf(key);
    if (index < 0) return;
    for (const later of order.slice(index)) this.showStep(later, true);
  }

  // --- errors ------------------------------------------------------------

  errorText(code) {
    const errors = this.strings.errors ?? {};
    return errors[code] ?? errors.invalid ?? code;
  }

  showErrors(errors) {
    this.clearAllErrors();
    const keys = Object.keys(errors);
    if (keys.length === 0) return;
    let marked = 0;
    for (const key of keys) {
      const target = this.querySelector(`[data-error-for="${key}"]`);
      if (target) {
        marked += 1;
        // The app answers a wrong tablet code with the generic "invalid"; the page has the plain sentence for it.
        const text = this.errorText(key === 'intakeCode' && errors[key] === 'invalid' ? 'staff_code' : errors[key]);
        // "Bitte mindestens {min} Fotos hochladen." showed the placeholder until 19.10.2026.
        target.textContent = fill(text, { min: this.limits?.minPhotos, max: this.limits?.maxPhotos });
        target.hidden = false;
      }
      const input = this.querySelector(`[data-field="${key}"]`);
      if (input && input.type !== 'radio') input.setAttribute('aria-invalid', 'true');
      for (const fieldset of this.querySelectorAll('[data-step]')) {
        if (fieldset.hidden && fieldset.querySelector(`[data-error-for="${key}"]`) && this.stepAppliesToChannel(fieldset.dataset.step)) fieldset.hidden = false;
      }
    }
    const summary = this.querySelector('[data-summary]');
    if (summary) {
      const first = FIELD_ORDER.find((k) => keys.includes(k)) ?? keys[0];
      summary.innerHTML = '';
      const text = document.createElement('p');
      // A code the page has no field for (an upload token, say) must not ask to check marks that are not there.
      text.textContent = (marked > 0 ? this.strings.fixErrors : this.strings.submitFailed) ?? '';
      summary.append(text);
      summary.hidden = false;
      summary.focus();
      this.querySelector(`[data-error-for="${first}"]`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }

  stepAppliesToChannel(step) {
    if (!PURCHASE_STEPS.includes(step)) return true;
    return (KIND_STEPS[purchaseKind(this.state)] ?? []).includes(step);
  }

  clearError(key) {
    const target = this.querySelector(`[data-error-for="${key}"]`);
    if (target) {
      target.hidden = true;
      target.textContent = '';
    }
    const input = this.querySelector(`[data-field="${key}"]`);
    if (input) input.removeAttribute('aria-invalid');
    // The summary goes with the last mark: "Bitte prüfe die markierten Felder." over a form without marks reads as a dead end.
    if (![...this.querySelectorAll('[data-error-for]')].some((e) => !e.hidden)) {
      const summary = this.querySelector('[data-summary]');
      if (summary) {
        summary.hidden = true;
        summary.innerHTML = '';
      }
    }
  }

  clearAllErrors() {
    for (const el of this.querySelectorAll('[data-error-for]')) {
      el.hidden = true;
      el.textContent = '';
    }
    for (const el of this.querySelectorAll('[aria-invalid]')) el.removeAttribute('aria-invalid');
    const summary = this.querySelector('[data-summary]');
    if (summary) {
      summary.hidden = true;
      summary.innerHTML = '';
    }
  }

  // --- order lookup -------------------------------------------------------

  async lookup() {
    const status = this.querySelector('[data-lookup-status]');
    const errors = {};
    if (!normalizeOrderName(this.state.orderName)) errors.orderName = 'invalid';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(this.state.orderEmail ?? '').trim())) errors.orderEmail = 'invalid';
    if (Object.keys(errors).length > 0) return this.showErrors(errors);
    this.clearError('orderName');
    this.clearError('orderEmail');
    // A new search (a corrected number) leaves "Trotzdem melden" again.
    this.lookupMissed = false;
    if (this.state.orderUnverified) this.setUnverified(false);
    else this.renderUnverified();
    if (status) {
      status.textContent = this.strings.lookupSearching ?? '';
      status.hidden = false;
    }
    let response;
    try {
      response = await this.fetchImpl(`${this.proxy}/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ orderName: normalizeOrderName(this.state.orderName), email: String(this.state.orderEmail).trim(), locale: this.locale }),
      });
    } catch {
      if (status) status.textContent = this.strings.lookupFailed ?? '';
      return;
    }
    if (response.status === 503) {
      if (status) status.textContent = this.strings.disabled ?? '';
      return;
    }
    if (response.status === 404) {
      if (status) status.hidden = true;
      this.renderItems(null);
      // Gift with another e-mail, a typo on the confirmation, an order beyond the app's window: offer the way with the receipt.
      this.lookupMissed = true;
      this.renderUnverified();
      return this.showErrors({ orderName: 'not_found' });
    }
    if (!response.ok) {
      if (status) status.textContent = this.strings.lookupFailed ?? '';
      return;
    }
    const data = await response.json();
    const order = data?.order;
    if (!order) {
      if (status) status.textContent = this.strings.lookupFailed ?? '';
      return;
    }
    this.lookedUp = order;
    if (status) status.textContent = fill(this.strings.lookupFound, { name: order.name, date: order.placedAt ? new Date(order.placedAt).toLocaleDateString(this.locale === 'en' ? 'en-GB' : 'de-DE') : '' });
    this.renderItems(order.items ?? []);
    this.prefillContact(order.contact);
  }

  renderItems(items) {
    const wrap = this.querySelector('[data-items]');
    const list = this.querySelector('[data-item-list]');
    if (!wrap || !list) return;
    list.innerHTML = '';
    this.state.itemKey = null;
    if (!items || items.length === 0) {
      wrap.hidden = true;
      return;
    }
    items.forEach((item, index) => {
      const label = document.createElement('label');
      label.className = 'complaint__item';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'itemKey';
      input.value = String(item.key);
      const media = item.imageUrl ? document.createElement('img') : document.createElement('span');
      if (item.imageUrl) {
        media.src = item.imageUrl;
        media.alt = '';
        media.loading = 'lazy';
      } else media.className = 'complaint__item-placeholder';
      const text = document.createElement('span');
      const title = document.createElement('span');
      title.className = 'complaint__item-title';
      title.textContent = item.title;
      const meta = document.createElement('span');
      meta.className = 'complaint__item-meta';
      meta.textContent = [item.vendor, item.variantTitle, item.sku, item.quantity > 1 ? fill(this.strings.lookupQuantity, { count: item.quantity }) : null].filter(Boolean).join(' · ');
      text.append(title, document.createElement('br'), meta);
      label.append(input, media, text);
      list.append(label);
      if (items.length === 1 && index === 0) {
        input.checked = true;
        this.state.itemKey = String(item.key);
      }
    });
    wrap.hidden = false;
    if (this.state.itemKey) this.revealFrom('serial');
  }

  prefillContact(contact) {
    if (!contact) return;
    const mapping = { customerName: contact.name, email: this.state.orderEmail, phone: contact.phone, address1: contact.address1, address2: contact.address2, zip: contact.zip, city: contact.city, countryCode: contact.countryCode };
    let changed = false;
    for (const [field, value] of Object.entries(mapping)) {
      const input = this.querySelector(`[data-field="${field}"]`);
      if (!input || value == null || value === '') continue;
      if (input.tagName === 'SELECT' && !Array.from(input.options).some((o) => o.value === value)) continue;
      if (String(input.value ?? '').trim() === '' || field !== 'email') {
        input.value = value;
        this.state[field] = value;
        changed = true;
      }
    }
    const note = this.querySelector('[data-contact-prefilled]');
    if (note) note.hidden = !changed;
  }

  // --- files -------------------------------------------------------------

  /** Uploads the chosen files one after the other; the submit waits for every run still going (this.uploads). */
  addFiles(kind, files) {
    const run = this.uploadFiles(kind, files);
    this.uploads.add(run);
    const done = () => this.uploads.delete(run);
    run.then(done, done);
    return run;
  }

  async uploadFiles(kind, files) {
    const max = kind === 'receipt' ? this.limits.maxReceipts : this.limits.maxPhotos;
    for (const file of files) {
      if (this.files[kind].length >= max) {
        this.showErrors({ [kind === 'receipt' ? 'receiptIds' : 'photoIds']: 'too_many' });
        break;
      }
      const entry = { file, status: 'waiting', id: null, previewUrl: null, name: file.name };
      this.files[kind].push(entry);
      this.renderFiles(kind);
      await this.uploadOne(kind, entry);
    }
    this.renderPhotoCount();
  }

  async uploadOne(kind, entry) {
    try {
      const isImage = /^image\//.test(entry.file.type || '') || /\.(jpe?g|png|webp|heic|heif)$/i.test(entry.file.name || '');
      const processed = isImage ? await this.imageProcessor(entry.file) : await this.documentProcessor(entry.file);
      entry.previewUrl = processed.previewUrl;
      entry.name = processed.fileName;
      this.renderFiles(kind);
      const response = await this.fetchImpl(`${this.proxy}/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ uploadToken: this.uploadToken, kind, fileName: processed.fileName, mimeType: processed.mimeType, base64: processed.base64 }),
      });
      if (response.status === 503) throw new Error('disabled');
      if (response.status === 413) throw new Error('too_large');
      if (response.status === 429) throw new Error('too_many');
      if (!response.ok) throw new Error('failed');
      const data = await response.json();
      entry.id = String(data.id);
      entry.status = 'done';
      this.syncIds(kind);
      this.clearError(kind === 'receipt' ? 'receiptIds' : 'photoIds');
    } catch (error) {
      entry.status = 'failed';
      entry.error = error instanceof Error ? error.message : 'failed';
    }
    this.renderFiles(kind);
    this.renderPhotoCount();
  }

  syncIds(kind) {
    const ids = this.files[kind].filter((e) => e.id).map((e) => e.id);
    if (kind === 'receipt') this.state.receiptIds = ids;
    else this.state.photoIds = ids;
  }

  removeFile(kind, entry) {
    this.files[kind] = this.files[kind].filter((e) => e !== entry);
    this.syncIds(kind);
    this.renderFiles(kind);
    this.renderPhotoCount();
  }

  fileStatusText(entry) {
    if (entry.status === 'done') return this.strings.uploadDone ?? '';
    if (entry.status === 'failed') {
      if (entry.error === 'unreadable') return this.strings.uploadUnreadable ?? '';
      if (entry.error === 'too_large') return this.strings.uploadTooLarge ?? '';
      if (entry.error === 'too_many') return this.strings.uploadTooMany ?? '';
      if (entry.error === 'disabled') return this.strings.disabled ?? '';
      return this.strings.uploadFailed ?? '';
    }
    return this.strings.uploadWaiting ?? '';
  }

  renderFiles(kind) {
    const list = this.querySelector(`[data-file-list="${kind}"]`);
    if (!list) return;
    list.innerHTML = '';
    for (const entry of this.files[kind]) {
      const item = document.createElement('li');
      item.className = `complaint__file complaint__file--${entry.status}`;
      if (entry.previewUrl) {
        const img = document.createElement('img');
        img.src = entry.previewUrl;
        img.alt = '';
        item.append(img);
      }
      const name = document.createElement('span');
      name.className = 'complaint__file-name';
      name.textContent = entry.name;
      const status = document.createElement('span');
      status.className = 'complaint__file-status';
      status.textContent = this.fileStatusText(entry);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'complaint__file-remove';
      remove.setAttribute('aria-label', this.strings.remove ?? 'remove');
      remove.textContent = '×';
      remove.addEventListener('click', () => this.removeFile(kind, entry));
      item.append(name, status, remove);
      list.append(item);
    }
  }

  /** "Noch 4 Zeichen" under the description while it is shorter than DESCRIPTION_MIN; hidden when empty or long enough. */
  renderDescriptionHint() {
    const target = this.querySelector('[data-description-hint]');
    if (!target) return;
    const missing = DESCRIPTION_MIN - String(this.state.defectDescription ?? '').trim().length;
    target.hidden = missing <= 0 || missing === DESCRIPTION_MIN;
    target.textContent = target.hidden ? '' : fill(this.strings.descriptionMissing ?? '', { count: missing, min: DESCRIPTION_MIN });
  }

  renderPhotoCount() {
    const target = this.querySelector('[data-photo-count]');
    if (!target || !this.limits) return;
    target.textContent = fill(this.strings.photoCount, { count: this.state.photoIds.length, min: this.limits.minPhotos, max: this.limits.maxPhotos });
  }

  // --- submit ------------------------------------------------------------

  async submit() {
    const status = this.querySelector('[data-submit-status]');
    const button = this.querySelector('[data-submit]');
    if (this.hasAttribute('data-busy')) return;
    if (this.uploads.size > 0) {
      // 19.10.2026: a submit while a photo was still on its way marked "too few photos"; the finished upload took the
      // mark away and left the summary over a form without marks. A photo on its way is waited for, then it is sent.
      this.setAttribute('data-busy', '');
      if (button) button.disabled = true;
      if (status) {
        status.textContent = this.strings.submitting ?? '';
        status.hidden = false;
      }
      while (this.uploads.size > 0) await Promise.allSettled([...this.uploads]);
      this.removeAttribute('data-busy');
      if (button) button.disabled = false;
      if (status) status.hidden = true;
    }
    const errors = validateLocal(this.state, this.limits, this.mode);
    if (Object.keys(errors).length > 0) return this.showErrors(errors);
    this.clearAllErrors();
    this.setAttribute('data-busy', '');
    if (button) button.disabled = true;
    if (status) {
      status.textContent = this.strings.submitting ?? '';
      status.hidden = false;
    }
    let response;
    try {
      response = await this.fetchImpl(`${this.proxy}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(buildPayload(this.state, this.uploadToken, this.locale, this.mode)),
      });
    } catch {
      response = null;
    }
    this.removeAttribute('data-busy');
    if (button) button.disabled = false;
    if (!response) {
      if (status) status.textContent = this.strings.submitFailed ?? '';
      return;
    }
    if (response.status === 503) {
      if (status) status.textContent = this.strings.disabled ?? '';
      return;
    }
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    if (response.status === 422 && data?.errors) {
      if (status) status.hidden = true;
      return this.showErrors(data.errors);
    }
    if (!response.ok || !data?.ok) {
      if (status) status.textContent = this.strings.submitFailed ?? '';
      return;
    }
    this.showSuccess(data.caseNumber);
  }

  showSuccess(caseNumber) {
    if (this.form) this.form.hidden = true;
    const success = this.querySelector('[data-success]');
    if (!success) return;
    const heading = success.querySelector('[data-success-heading]');
    const text = success.querySelector('[data-success-text]');
    const code = success.querySelector('[data-success-case]');
    const internal = this.state.channel === 'internal';
    const email = String(this.state.email ?? '').trim();
    if (heading) heading.textContent = this.strings.successHeading ?? '';
    const staffText = internal || !email ? this.strings.successTextInternal : this.state.itemWhere === 'customer' ? this.strings.successTextStaffCustomer ?? this.strings.successTextStaff : this.strings.successTextStaff;
    if (text) text.textContent = this.mode === 'staff' ? fill(staffText, { email }) : fill(this.strings.successText, { email });
    if (code) code.textContent = caseNumber;
    this.fillSlip(caseNumber);
    success.hidden = false;
    success.focus?.();
    success.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    this.dispatchEvent(new CustomEvent('complaint:submitted', { bubbles: true, detail: { caseNumber } }));
  }

  /** The paper slip's fields for the item that stays in the house (staff page only). */
  fillSlip(caseNumber) {
    const slip = this.querySelector('[data-slip]');
    if (!slip) return;
    const s = this.state;
    const picked = (this.lookedUp?.items ?? []).find((i) => String(i.key) === String(s.itemKey));
    const item = purchaseKind(s) === 'online' && picked ? [picked.vendor, picked.title, picked.variantTitle].filter(Boolean).join(' ') : [s.manufacturer, s.itemTitle, s.variantTitle].map((x) => String(x ?? '').trim()).filter(Boolean).join(' ');
    const locationNames = { ronsberg: 'Ronsberg', pfronten: 'Pfronten' };
    const values = {
      case: caseNumber,
      date: new Date().toLocaleDateString(this.locale === 'en' ? 'en-GB' : 'de-DE'),
      'intake-by': String(s.intakeBy ?? '').trim(),
      location: s.itemWhere === 'house' || s.channel === 'internal' ? locationNames[s.itemLocation] ?? String(s.itemLocation ?? '') : this.strings.slipItemWithCustomer ?? '',
      item,
      serial: s.serialMissing ? '' : String(s.serialNumber ?? '').trim(),
      customer: s.channel === 'internal' ? this.strings.slipNoCustomer ?? '' : String(s.customerName ?? '').trim(),
      phone: String(s.phone ?? '').trim(),
      reason: String(s.defectDescription ?? '').trim().slice(0, 300),
    };
    for (const [key, value] of Object.entries(values)) {
      const cell = slip.querySelector(`[data-slip-${key}]`);
      if (cell) cell.textContent = value;
    }
    // No customer, no phone and no customer's signature.
    for (const el of slip.querySelectorAll('[data-slip-customer-only]')) el.hidden = s.channel === 'internal';
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('complaint-form')) {
  customElements.define('complaint-form', ComplaintForm);
}
